/* Lead system constants and helpers — ported verbatim from the OLD
   admin source (AdminPanel.jsx lines 167–337). All exports preserve
   the OLD admin's behavior 1:1 so Dashboard / LeadsList / KanbanBoard /
   LeadDetail / LeadForm work as before. */

// ── assignedTo normalisers ────────────────────────────────────────────
// Lead.assignedTo can be: null, a single id string, or an array of ids.
// Both helpers normalise so callers don't need scattered `Array.isArray`
// checks. Legacy data may carry the string shape; new data writes arrays.
export function getAssignees(lead) {
  if (!lead || lead.assignedTo == null) return [];
  if (Array.isArray(lead.assignedTo)) return lead.assignedTo.filter(Boolean);
  return lead.assignedTo ? [lead.assignedTo] : [];
}
export function isAssignedTo(lead, id) {
  return getAssignees(lead).includes(id);
}

// ── Lead status registry ──────────────────────────────────────────────
// Kebab-case codes for storage; STATUS_META labels are for display.
// `let` not `const` so the Lead Settings screen can append custom statuses
// at runtime. A `statusVersion` counter at the AppDataContext root forces
// consumers to re-render when the registry mutates.
export let STATUSES = ["new", "contacted", "follow_up", "visited", "re_visit", "weekend", "token", "booked", "hold", "lead_lost"];
export let STATUS_META = {
  new:        { label: "New",        cls: "bg-blue-50 text-blue-700 ring-blue-600/20" },
  contacted:  { label: "Contacted",  cls: "bg-slate-100 text-slate-700 ring-slate-500/20" },
  follow_up:  { label: "Follow-up",  cls: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  visited:    { label: "Visited",    cls: "bg-violet-50 text-violet-700 ring-violet-600/20" },
  re_visit:   { label: "Re-visit",   cls: "bg-purple-50 text-purple-700 ring-purple-600/20" },
  weekend:    { label: "Weekend",    cls: "bg-cyan-50 text-cyan-700 ring-cyan-600/20" },
  token:      { label: "Token",      cls: "bg-teal-50 text-teal-700 ring-teal-600/20" },
  booked:     { label: "Booked",     cls: "bg-green-50 text-green-700 ring-green-600/20" },
  hold:       { label: "Hold",       cls: "bg-yellow-50 text-yellow-700 ring-yellow-600/20" },
  lead_lost:  { label: "Lead Lost",  cls: "bg-red-50 text-red-700 ring-red-600/20" },
};

// Cycled in order of addition so each custom status gets a visually distinct
// chip without the admin picking a colour.
export const STATUS_PALETTE = [
  "bg-pink-50 text-pink-700 ring-pink-600/20",
  "bg-orange-50 text-orange-700 ring-orange-600/20",
  "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  "bg-sky-50 text-sky-700 ring-sky-600/20",
  "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20",
  "bg-lime-50 text-lime-700 ring-lime-600/20",
  "bg-rose-50 text-rose-700 ring-rose-600/20",
  "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
];

/* Palette token -> Tailwind classes.

   Every class string is written out literally so Tailwind's JIT can find them
   by scanning this file. A class assembled at runtime from a database value
   (`bg-${color}-50`) would never be generated and the badge would render
   unstyled. The database stores only the token. */
export const COLOR_CLASSES = {
  blue:    "bg-blue-50 text-blue-700 ring-blue-600/20",
  slate:   "bg-slate-100 text-slate-700 ring-slate-500/20",
  amber:   "bg-amber-50 text-amber-700 ring-amber-600/20",
  violet:  "bg-violet-50 text-violet-700 ring-violet-600/20",
  purple:  "bg-purple-50 text-purple-700 ring-purple-600/20",
  cyan:    "bg-cyan-50 text-cyan-700 ring-cyan-600/20",
  teal:    "bg-teal-50 text-teal-700 ring-teal-600/20",
  green:   "bg-green-50 text-green-700 ring-green-600/20",
  yellow:  "bg-yellow-50 text-yellow-700 ring-yellow-600/20",
  red:     "bg-red-50 text-red-700 ring-red-600/20",
  pink:    "bg-pink-50 text-pink-700 ring-pink-600/20",
  orange:  "bg-orange-50 text-orange-700 ring-orange-600/20",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  sky:     "bg-sky-50 text-sky-700 ring-sky-600/20",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20",
  lime:    "bg-lime-50 text-lime-700 ring-lime-600/20",
  rose:    "bg-rose-50 text-rose-700 ring-rose-600/20",
  indigo:  "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
};

/* Replace the in-memory registry with the server's list.

   Called once at boot from AppDataContext, and again after any create/update/
   delete. Because STATUSES and STATUS_META are `let` exports, ES module live
   bindings propagate the reassignment to every importer — StatusBadge,
   LeadsList, LeadDetail, KanbanBoard, LeadForm and LeadSettings all pick it up
   without changes. The statusVersion bump forces the re-render. */
export function setLeadStatuses(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  STATUSES = rows.map((r) => r.code);
  STATUS_META = rows.reduce((acc, r) => {
    acc[r.code] = {
      label: r.label,
      cls: COLOR_CLASSES[r.color] || COLOR_CLASSES.slate,
      custom: !r.isBuiltin,
      active: r.active !== false,
      sortOrder: r.sortOrder,
    };
    return acc;
  }, {});
  return true;
}

// Form dropdowns — fixed taxonomies the OLD admin ships with.
// Mirrors the API's LeadSource enum. "Old-Data" keeps its hyphen on
// purpose -- leadsApi.js translates it to/from the enum's Old_Data.
//
// "Website" was missing. Leads from the public advisor card are stored
// as Website, but the edit form found no matching option and fell back
// to the form default ("Meta"), rewriting the source of a real web
// enquiry on save. The LeadsList source filter reads this same array,
// so filtering by Website was impossible too.
export const SOURCES = ["Website", "Meta", "Google", "YouTube", "Old-Data", "Reference", "WhatsApp", "SMS", "Other"];
export const CONFIGS = ["1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "4 BHK", "5 BHK", "Duplex", "Plot", "Office", "Shop", "Showroom"];

/* Per-property-type config options for LEADS.

   CONFIGS (above) is the legacy flat list — it mixes property TYPES
   ("Duplex", "Plot") in with actual configs, offers "2 BHK" on a plot
   enquiry, and has no area ranges at all. It stays exported because
   seed.js and older screens still reference it.

   These lists were derived from what is actually stored in leads.config
   in production, not invented. Notable: 2.5 BHK is in real use (14 leads)
   and plot leads DO record configs, as area ranges (17 leads) — so the
   property wizard's hideConfigDropdown rule does not apply here.

   2026-07-31: aligned with propertyTypeSchema.js so a lead can record
   interest in any configuration a property can actually have - halves up to
   4.5, sizes to 9 BHK+, "Studio" rather than "1 RK", "Shops"/"Office" rather
   than "Shop"/"Office Space". Leads stored under the older spellings keep
   showing them: the legacy branch in LeadForm renders any stored-but-unlisted
   value as a ticked amber chip, so editing never drops it. Duplex and plot
   are unchanged.
   The odd ones out are NOT rewritten in the DB — LeadForm keeps any
   stored-but-unlisted value visible and ticked so editing never drops it.

   Keys are the LOWERCASE property.type values the leads API returns,
   not the capitalised ones propertyTypeSchema.js uses for the wizard. */
export const LEAD_CONFIGS = {
  apartment:  ["1 RK", "1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "4.5 BHK", "5 BHK", "6 BHK", "7 BHK", "8 BHK", "9 BHK+"],
  villa:      ["1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "4.5 BHK", "5 BHK", "6 BHK", "7 BHK", "8 BHK", "9 BHK+"],
  duplex:     ["1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "4.5 BHK", "5 BHK", "6 BHK+"],
  studio:     ["Studio"],
  commercial: ["Shops", "Showroom", "Office"],
  plot:       ["Up to 1000 sq.ft", "1000\u20132000 sq.ft", "2000\u20134000 sq.ft", "4000+ sq.ft"],
};

/* Union, used when no property is linked (project blank or unmatched). */
export const ALL_LEAD_CONFIGS = Array.from(
  new Set(Object.values(LEAD_CONFIGS).flat())
);

export function configsForLeadType(type) {
  return LEAD_CONFIGS[type] || ALL_LEAD_CONFIGS;
}
export const POSSESSIONS = ["Under Construction", "Nearing", "Ready", "New Launching"];
export const OCCUPATIONS = ["Salaried", "Self-employed", "Business Owner", "Professional", "Retired", "Student", "Homemaker", "Other"];
export const BUYING_PURPOSES = ["Self-use", "Investment", "Rental Income"];

// Lead temperature — sales-team's quick read on how warm a prospect is.
// Both sales and admin/super_admin can edit this; it's the only field
// besides status and follow-up that the restricted sales-role edit form
// exposes.
export const TEMPERATURES = ["hot", "warm", "cold"];
export const TEMPERATURE_META = {
  hot:  { label: "Hot",  cls: "bg-red-50 text-red-700 ring-red-600/20" },
  warm: { label: "Warm", cls: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  cold: { label: "Cold", cls: "bg-sky-50 text-sky-700 ring-sky-600/20" },
};

// "Terminal" statuses — no follow-up obligation. Existing follow-up dates
// stay on the record (audit) but are suppressed in Today's Follow-up KPI,
// schedule card, overdue highlight, and the LeadForm follow-up scheduler.
export const TERMINAL_STATUSES = new Set(["token", "booked", "lead_lost"]);
export const allowsFollowUp = (status) => !TERMINAL_STATUSES.has(status);

// Sort options for the leads list. Keys map to per-row date fields; `dir`
// drives ascending vs descending. "default" preserves source ordering.
// "Last Status Change Date" reads `lastStatusChangeAt`; "Last Follow-up
// Taken Date" reads `lastFollowUpAt` (the historical completion, not the
// scheduled `followUpAt`).
export const SORT_OPTIONS = [
  { value: "default",                 label: "Default" },
  { value: "lastStatusChangeAt-asc",  label: "Last Status Change Date (Oldest First)",   field: "lastStatusChangeAt", dir: 1 },
  { value: "lastStatusChangeAt-desc", label: "Last Status Change Date (Newest First)",   field: "lastStatusChangeAt", dir: -1 },
  { value: "createdAt-asc",           label: "Created Date (Oldest First)",              field: "createdAt",          dir: 1 },
  { value: "createdAt-desc",          label: "Created Date (Newest First)",              field: "createdAt",          dir: -1 },
  { value: "followUpAt-asc",          label: "Next Follow-up Date (Oldest First)",       field: "followUpAt",         dir: 1 },
  { value: "followUpAt-desc",         label: "Next Follow-up Date (Newest First)",       field: "followUpAt",         dir: -1 },
  { value: "lastFollowUpAt-asc",      label: "Last Follow-up Taken Date (Oldest First)", field: "lastFollowUpAt",     dir: 1 },
  { value: "lastFollowUpAt-desc",     label: "Last Follow-up Taken Date (Newest First)", field: "lastFollowUpAt",     dir: -1 },
];

// ── Calendar helpers ──────────────────────────────────────────────────
export const isToday = (d) => {
  if (!d) return false;
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return d >= start && d < end;
};

export const configLabel = (c) => {
  if (Array.isArray(c)) return c.join(" / ");
  return c || "";
};

// Property-type label for the leads Type column/filter.
export const PROPERTY_TYPE_LABELS = {
  apartment: "Apartment", villa: "Villa", plot: "Plot",
  duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial",
};
export const PROPERTY_TYPE_OPTIONS = [
  "apartment", "villa", "plot", "duplex", "studio", "commercial",
];

// ── isOverdue: paints the whole lead row red in list and grid views.
// Accepts a raw Date OR a full lead object; for leads, also skips
// terminal-status records.
export const isOverdue = (input) => {
  if (!input) return false;
  if (input instanceof Date) return input < new Date();
  if (!input.followUpAt) return false;
  if (TERMINAL_STATUSES.has(input.status)) return false;
  return input.followUpAt < new Date();
};

// ── Display helpers ───────────────────────────────────────────────────
export const fmtDate = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
export const fmtTime = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
export const since = (d) => {
  const h = Math.floor((Date.now() - d.getTime()) / 36e5);
  if (h < 1) return "just now";
  if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
};

// ── Phone display masking — sales-role redaction of customer numbers ──
// Customer mobile numbers carry +91 in storage; we mask the middle 5
// digits for the sales view. Admin and super_admin see the full number.
export function maskPhone(p) { return p.slice(0, 6) + " ••••• " + p.slice(-2); }

export function isValidYoutubeUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  return /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[A-Za-z0-9_-]{6,}/.test(trimmed);
}

// ── Clipboard fallback ────────────────────────────────────────────────
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed"; ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } catch { /* noop */ }
  document.body.removeChild(ta);
  return true;
}

// ── CSV parser ─────────────────────────────────────────────────────────
// Returns string[][]. Handles quoted fields, escaped quotes ("") inside
// quoted fields, and both \r\n and \n line endings. Inline so we don't
// pull in papaparse for one use site.
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQ = false;
      } else cell += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { row.push(cell); cell = ""; }
      else if (ch === "\r") { /* skip — \r\n handled by \n */ }
      else if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
      else cell += ch;
    }
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

// ── Seed: lead generator ──────────────────────────────────────────────
// Verbatim from OLD admin lines 336–411. Produces 48 leads by default
// with realistic distribution across statuses, follow-up windows, and
// assigned reps. First 6 are forced into the past 18h so Today's KPI
// fires on first render; 4 are forced overdue so the red-row treatment
// is visible immediately.
const PROJECTS = [
  "Lodha Belmondo", "Godrej Splendour", "Kolte Patil iTowers", "VTP Altair",
  "Mahindra Citadel", "Godrej River Greens", "Rohan Abhilasha", "Pride World City",
];
const NAMES = [
  "Arjun Sharma", "Priya Kulkarni", "Rohan Iyer", "Sneha Patil", "Vikram Desai",
  "Ananya Joshi", "Karan Mehta", "Divya Nair", "Rahul Verma", "Pooja Reddy",
  "Siddharth Rao", "Meera Bhat", "Aditya Kumar", "Nisha Shah", "Tarun Gupta",
  "Kavya Menon", "Aakash Singh", "Ritu Agarwal", "Manish Pillai", "Shreya Das",
];

export function genLeads(n, salesTeam) {
  const team = salesTeam || [{ id: "s1" }, { id: "s2" }, { id: "s3" }, { id: "s4" }];

  // Deterministic terminal-status injection: certain indices get forced to
  // booked / token / hold / lead_lost so the Dashboard's Converted KPI and
  // the LeadsList's terminal facets always have signal on a fresh load.
  // OLD admin's pristine seed left these at 0; the actual OLD admin sessions
  // accumulated them through manual testing. We bake them in so demos and
  // first-runs look lived-in. Active-pipeline statuses still pick randomly
  // from indices 0–5 for the rest, preserving OLD admin's distribution.
  const forcedStatus = {
    // 4 booked — fills out the Converted Leads KPI for sales role + admin views.
    8: "booked",  17: "booked",  29: "booked",  41: "booked",
    // 3 tokens — leads that paid token money, awaiting full booking.
    11: "token",  24: "token",   38: "token",
    // 2 on hold — pending decision / paused.
    16: "hold",   33: "hold",
    // 2 lost — closed-out for negative reasons.
    22: "lead_lost", 45: "lead_lost",
  };

  const out = [];
  for (let i = 0; i < n; i++) {
    const created = i < 6
      ? new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 18)
      : new Date(Date.now() - (1 + Math.random() * 13) * 1000 * 60 * 60 * 24);
    let followUpAt = null;
    const r = Math.random();
    if (i < 4) {
      followUpAt = new Date(Date.now() - (1 + Math.random() * 47) * 60 * 60 * 1000);
    } else if (r < 0.18) {
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      const minsIntoDay = 9 * 60 + Math.floor(Math.random() * 9 * 60);
      followUpAt = new Date(todayStart.getTime() + minsIntoDay * 60 * 1000);
    } else if (r < 0.30) {
      followUpAt = new Date(Date.now() + (1 + Math.random() * 7) * 24 * 60 * 60 * 1000);
    }
    const mobile10 = String(9000000000 + Math.floor(Math.random() * 999999999)).slice(0, 10);
    const wantsSeparateWA = Math.random() < 0.2;
    const wa10 = wantsSeparateWA ? String(9000000000 + Math.floor(Math.random() * 999999999)).slice(0, 10) : "";
    const primaryConfig = CONFIGS[Math.floor(Math.random() * 7)];
    const config = Math.random() < 0.3
      ? [primaryConfig, CONFIGS[Math.floor(Math.random() * 7)]].filter((c, j, a) => a.indexOf(c) === j)
      : [primaryConfig];
    const budgetCrores = (0.6 + Math.random() * 4).toFixed(2);
    const possessionPick = POSSESSIONS.filter(() => Math.random() > 0.5);
    out.push({
      id: "L" + (1000 + i),
      name: NAMES[i % NAMES.length],
      phone: "+91 " + mobile10.slice(0, 5) + " " + mobile10.slice(5),
      mobile: mobile10,
      whatsapp: wa10,
      email: NAMES[i % NAMES.length].toLowerCase().replace(/\s/g, ".") + "@gmail.com",
      occupation: OCCUPATIONS[Math.floor(Math.random() * 5)],
      companyName: Math.random() > 0.4 ? ["Infosys", "TCS", "Wipro", "Bajaj", "Persistent", "Tata"][Math.floor(Math.random() * 6)] : "",
      project: PROJECTS[Math.floor(Math.random() * PROJECTS.length)],
      source: SOURCES[Math.floor(Math.random() * SOURCES.length)],
      assignedTo: Math.random() > 0.25 ? team[Math.floor(Math.random() * team.length)].id : null,
      status: forcedStatus[i] || STATUSES[Math.floor(Math.random() * 6)],
      config,
      budget: "₹" + budgetCrores + " Cr",
      buyingPurpose: BUYING_PURPOSES[Math.floor(Math.random() * BUYING_PURPOSES.length)],
      reference: "",
      address: Math.random() > 0.6 ? ["Baner", "Hinjewadi", "Wakad", "Kothrud", "Aundh"][Math.floor(Math.random() * 5)] + ", Pune" : "",
      remark: "",
      note: "",
      possession: possessionPick.length ? possessionPick : ["Under Construction"],
      temperature: TEMPERATURES[Math.floor(Math.random() * TEMPERATURES.length)],
      createdAt: created,
      lastActivity: new Date(created.getTime() + Math.random() * 1000 * 60 * 60 * 48),
      followUpAt,
      lastStatusChangeAt: new Date(created.getTime() + Math.random() * 1000 * 60 * 60 * 72),
      lastFollowUpAt: Math.random() > 0.4 ? new Date(created.getTime() + Math.random() * 1000 * 60 * 60 * 120) : null,
    });
  }
  return out;
}
